// The universal search's deck list: names only (plan nav-search-2026-10-16,
// "Your decks"). useSavedDecks loads every row with its full analysis
// (select('*')), which is far too heavy for a search box, so this reads just
// the fields the search matches and shows: id, name, commander, partner,
// bracket and the save time. Signed in: the owner's saved_decks rows,
// filtered by user_id explicitly (the public-read RLS policy would otherwise
// let other users' public decks through, as useSavedDecks notes). Guest: the
// local list the guest save path writes.
import { supabase } from './supabase'
import { SAVED_DECKS_STORAGE_KEY, type DeckOwnership } from '../hooks/useSavedDecks'

export interface DeckNameRow {
  id: string
  name: string | null
  commander: string | null
  partner: string | null
  bracket: number | null
  savedAt: string
  /** The stored ownership code; absent = unknown, which segmentOf reads as owned. */
  ownership?: DeckOwnership
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null
}

function ownershipOf(v: unknown): DeckOwnership | undefined {
  // eslint-disable-next-line i18next/no-literal-string -- stored ownership codes
  return v === 'saved' ? 'saved' : v === 'owned' ? 'owned' : undefined
}

function fromLocal(): DeckNameRow[] {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(SAVED_DECKS_STORAGE_KEY)
  } catch {
    return []
  }
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const out: DeckNameRow[] = []
    for (const d of parsed) {
      if (!d || typeof d !== 'object') continue
      const r = d as Record<string, unknown>
      const id = str(r.id)
      if (!id) continue
      out.push({
        id,
        name: str(r.name),
        commander: str(r.commander),
        partner: str(r.partner),
        bracket: typeof r.bracket === 'number' ? r.bracket : null,
        savedAt: str(r.savedAt) ?? '',
        ownership: ownershipOf(r.ownership),
      })
    }
    return out
  } catch {
    return []
  }
}

/** The deck list for `userId` (null = guest), names only, newest first. */
export async function fetchDeckNameIndex(userId: string | null): Promise<DeckNameRow[]> {
  if (!userId || !supabase) {
    return fromLocal().sort((a, b) => b.savedAt.localeCompare(a.savedAt))
  }
  const { data, error } = await supabase
    .from('saved_decks')
    .select('id, name, commander, partner, bracket, created_at, updated_at, ownership')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false, nullsFirst: false })
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).flatMap(r => {
    const id = str(r.id)
    if (!id) return []
    return [{
      id,
      name: str(r.name),
      commander: str(r.commander),
      partner: str(r.partner),
      bracket: typeof r.bracket === 'number' ? r.bracket : null,
      savedAt: str(r.updated_at) ?? str(r.created_at) ?? '',
      ownership: ownershipOf(r.ownership),
    }]
  })
}

/** What Add to deck needs of ONE deck: its list and its command zone. */
export interface DeckForAdd {
  id: string
  name: string | null
  commander: string | null
  partner: string | null
  decklist: string
}

/**
 * The deck row as stored now, for the current owner (the picker's quantity
 * preview). null when the deck is gone. Throws when it cannot be read.
 */
export async function fetchDeckForAdd(id: string, userId: string | null): Promise<DeckForAdd | null> {
  if (!userId || !supabase) {
    let raw: string | null = null
    try { raw = localStorage.getItem(SAVED_DECKS_STORAGE_KEY) } catch { raw = null }
    const list: unknown = raw ? JSON.parse(raw) : []
    if (!Array.isArray(list)) return null
    const d = list.find(x => x && typeof x === 'object' && (x as { id?: unknown }).id === id) as Record<string, unknown> | undefined
    if (!d) return null
    return { id, name: str(d.name), commander: str(d.commander), partner: str(d.partner), decklist: str(d.decklist) ?? '' }
  }
  const { data, error } = await supabase
    .from('saved_decks')
    .select('id, name, commander, partner, decklist')
    .eq('id', id)
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const r = data as Record<string, unknown>
  return { id, name: str(r.name), commander: str(r.commander), partner: str(r.partner), decklist: str(r.decklist) ?? '' }
}
